import { isLookSystem } from "@/utils/look-gate";
import { boxCss } from "@/purpose-sections/length";

const spacingOf = (theme: unknown): Record<string, unknown> => {
  if (!isLookSystem(theme)) return {};
  const spacing = (theme as { styleSystem?: { spacing?: unknown } }).styleSystem
    ?.spacing;
  return spacing && typeof spacing === "object"
    ? (spacing as Record<string, unknown>)
    : {};
};

/**
 * Explicit spacing on a look-system website (`theme.styleSystem.spacing.explicitSpacing: true`): a section's Luft
 * indeni (`ui.padding`) and Luft udenom (`ui.margin`), and its content area's (`ui.content.padding`, `.margin`), are
 * 0 where none is stored, whatever its type. A website gets the marker from `explicitSpacingUpgrade`, which first
 * writes into every section the spacing it is drawn with, so the upgrade changes nothing on the screen. The CMS
 * mirrors this module and copies explicit-spacing.vectors.ts verbatim, so both must agree on every vector.
 */
export function hasExplicitSpacing(theme: unknown): boolean {
  return spacingOf(theme).explicitSpacing === true;
}

/**
 * Indrammet's step of explicit spacing (`styleSystem.spacing.explicitFrame: true` beside `explicitSpacing: true`): a
 * framed section's margin is its Luft udenom, and the frame draws none of its own. It has a marker of its own because
 * websites were upgraded before the step existed; with `explicitSpacing` alone the frame keeps drawing its margin over
 * Luft udenom until `explicitSpacingUpgrade` writes that margin.
 */
export function hasExplicitFrame(theme: unknown): boolean {
  return hasExplicitSpacing(theme) && spacingOf(theme).explicitFrame === true;
}

/**
 * The explicit spacing the CMS section preview reports this renderer draws (its ready message's `explicitSpacing`). 2:
 * it also draws Indrammet's step (`hasExplicitFrame`), so the CMS may write the whole upgrade, both markers included. A
 * CMS that knows only `true` never upgrades against it.
 */
export const EXPLICIT_SPACING_DRAWN = 2;

/**
 * Luft indeni of a layered section (layered-frame.ts) without its own on the look system: Tæthed's md step at the top
 * and bottom on a phone and its xl step from 768 px, so Tæthed keeps scaling them, and fixed 16/32 px sides.
 */
export const LAYERED_PADDING = {
  mobile: "space-md 16px",
  desktop: "space-xl 32px",
} as const;
const LAYERED = `${LAYERED_PADDING.mobile} | ${LAYERED_PADDING.desktop}`;

/**
 * The `ui.padding` each section type is drawn with on a look-system website where it stores none, in Luft indeni's
 * own tokens; `null` for the types without Luft indeni. Without a value every type's `ui.margin` and its content
 * area's padding and margin are 0, on every website, but a framed section's margin (FRAME_MARGIN). Two types depend
 * on more than their type: see `legacySpacing`.
 */
export const SECTION_PADDING_DEFAULTS: Readonly<Record<string, string | null>> =
  {
    BenefitsSection: LAYERED,
    QuoteCarouselSection: LAYERED,
    ProcessSection: LAYERED,
    FaqSection: LAYERED,
    QuickLinksSection: LAYERED,
    ContactBandSection: LAYERED,
    StatisticsSection: LAYERED,
    AreaGuideSection: LAYERED,
    PackagesSection: LAYERED,
    DownloadsSection: LAYERED,
    LogoListSection: LAYERED,
    ComparisonSection: LAYERED,
    EventSection: LAYERED,
    ImageTextBlockSection: LAYERED,
    TextBlockSection: LAYERED,
    FormIntroSection: LAYERED,
    StoreContactSection: LAYERED,
    VideoStorySection: LAYERED,
    ImageGallerySection: LAYERED,
    ContactPersonSection: LAYERED,
    TestimonialsSection: LAYERED,
    RecommendationsSection: LAYERED,
    StoreCardSection: LAYERED,
    TeamSection: LAYERED,
    ArticleListSection: LAYERED,
    PropertySelectionSection: LAYERED,
    PropertyListSection: LAYERED,
    ManualListingsSection: LAYERED,
    EmbedSection: LAYERED,
    StoreDisclosureSection: LAYERED,
    MediaMosaicSection: LAYERED,
    ImageTextAdvancedSection: LAYERED,
    BeforeAfterSection: LAYERED,
    ArticleDetailsSection: "md | lg",
    PropertySearchSection: "md | lg",
    SocialPostsSection: "md | lg",
    Tekst: "md | lg",
    Emnevisning: "md | lg",
    Hightlights: "md | lg",
    "Video med tekst, CTA": null,
    Medarbejder: "md | lg",
    "Foto med tekst": "none",
    ImageSectionV2Component: "none",
    "Fokus bolig": "none",
    FocusBandSection: "none",
    Kontaktformular: "md | lg",
    CaseList: "none",
    Søgning: "none",
    "Vurdering formular": "md | lg",
    Kontakt: "md | lg",
    Butikspræsentation: "md | lg",
    StoreSectionComponent: "md | lg",
    Fotos: "none",
    GallerySectionComponent: "none",
    Video: null,
    Køberkartoteksformular: "md | lg",
    Flexbånd: "md | lg",
    Boligsidenbadge: "md | lg",
    CaseTileGallerySection: "none",
    CaseContactSection: "md | lg",
    CasePresentationSection: "md | lg",
    CaseFinancialSection: "md | lg",
    CaseMapSection: "md | lg",
    CaseMapDeluxeSection: "md | lg",
    CaseClassicPresentationSection: "md | lg",
    CaseSliderGallerySection: "none",
    CaseImageGridSection: "md | lg",
    ImageTextSectionComponent: "none",
    TestimonySection: "md | lg",
    ImageCtaSection: "none",
    ImageContentSectionComponent: "none",
    ContentSectionComponent: "none",
    SplitContentSectionComponent: "none",
    PartnershipAgreementSection: "md | md",
    CompletePartnershipAgreementSection: "md | md",
    PrivacyPolicySection: "md | lg",
    CookiePolicySection: "md | lg",
    DynamicSectionComponent: "none",
    FooterWorkspaceSection: null,
    HtmlSectionComponent: "md | lg",
    CustomHtmlSection: "none",
  };

/** Vurdering formular without any `ui` is drawn with this padding instead of its type's. */
export const ASSESSMENT_FORM_WITHOUT_UI = "lg";

/**
 * Sagsfinansiering on a website with the Sekundær layout keeps 144 px (phone) and 80 px (from 768 px) under its photo
 * while its `ui.padding` is absent or empty; with explicit spacing that room is drawn only as its Luft indeni.
 */
export const FINANCIAL_ROOM = {
  withDefault: "16px 16px 160px | 32px 32px 112px",
  withoutPadding: "0px 0px 144px | 0px 0px 80px",
} as const;

/**
 * Indrammet's margin on every side (look-page.css) without a valid `ui.frameMargin`: 12 px on a phone and 24 px from
 * 768 px. Without the frame marker (`hasExplicitFrame`) it replaces the section's Luft udenom; with it the frame draws
 * none and Luft udenom is the frame's margin.
 */
export const FRAME_MARGIN = { mobile: "12px", desktop: "24px" } as const;
const FRAME_DEFAULT = `${FRAME_MARGIN.mobile} | ${FRAME_MARGIN.desktop}`;
const OWN_FRAME_MARGIN = /^\d{1,2}(?:\.\d{1,2})?px$/;
const OWN_FRAME_MARGIN_MAX = 64;

/** `ui.frameMargin` where the frame draws it: px with at most two decimals, up to 64; undefined otherwise. */
export function ownFrameMargin(value: unknown): string | undefined {
  return typeof value === "string" &&
    OWN_FRAME_MARGIN.test(value) &&
    parseFloat(value) <= OWN_FRAME_MARGIN_MAX
    ? value
    : undefined;
}

type Ui = Record<string, unknown>;
export interface SpacingWebsite {
  theme?: unknown;
  layout?: { name?: unknown } | null;
}
export interface SpacingSection {
  type: string;
  ui?: unknown;
}
/** What the upgrade writes into a section's `ui`: Luft indeni, Luft udenom or both. */
export interface WrittenSpacing {
  padding?: string;
  margin?: string;
}

const record = (value: unknown): Ui =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Ui)
    : {};

/** A layered section's padding with every screen it draws Tæthed's default on written out; undefined when none does. */
function layeredPadding(raw: unknown): string | undefined {
  const box = boxCss(raw);
  if (box.mobile && box.desktop) return undefined;
  const parts = typeof raw === "string" ? raw.split("|") : [];
  const mobile = box.mobile ? parts[0].trim() : LAYERED_PADDING.mobile;
  const desktop = box.desktop
    ? (parts[1] ?? parts[0]).trim()
    : LAYERED_PADDING.desktop;
  return `${mobile} | ${desktop}`;
}

/** The Luft indeni the section is drawn with where it relies on its type's (see `legacySpacing`). */
function legacyPadding(
  section: SpacingSection,
  website: SpacingWebsite,
  fallback: string,
): string | undefined {
  const ui = record(section.ui);
  if (fallback === LAYERED) return layeredPadding(ui.padding);
  const own = Object.hasOwn(ui, "padding");
  if (
    section.type === "CaseFinancialSection" &&
    record(website.layout).name === "secondary" &&
    !ui.padding
  )
    return own ? FINANCIAL_ROOM.withoutPadding : FINANCIAL_ROOM.withDefault;
  if (own) return undefined;
  if (section.type === "Vurdering formular" && section.ui === undefined)
    return ASSESSMENT_FORM_WITHOUT_UI;
  return fallback === "none" ? undefined : fallback;
}

/** A framed section's margin, which is the frame's whatever Luft udenom stores; undefined when that is stored. */
function framedMargin(section: SpacingSection): string | undefined {
  const ui = record(section.ui);
  if (ui.frame !== "framed") return undefined;
  const margin = ownFrameMargin(ui.frameMargin) ?? FRAME_DEFAULT;
  return ui.margin === margin ? undefined : margin;
}

/**
 * What the upgrade writes into one section's `ui` so that it looks the same with both markers as `website` draws it
 * now, or undefined when it relies on nothing: its Luft indeni where it relies on its type's, unless the website has
 * explicit spacing already, and a framed section's (Indrammet) margin as its Luft udenom, unless the website has the
 * frame marker. A stored value is never replaced, except a layered section's Luft indeni that the renderer reads as
 * absent on a screen (empty, more than four lengths) and Sagsfinansiering's empty one under the Sekundær layout, which
 * are drawn with a default without explicit spacing, and a framed section's Luft udenom, which the frame's margin
 * replaces without the frame marker. Every type with Luft indeni draws the frame; the ones without (`null`) draw
 * neither.
 */
export function legacySpacing(
  section: SpacingSection,
  website: SpacingWebsite,
): WrittenSpacing | undefined {
  if (!isLookSystem(website.theme) || hasExplicitFrame(website.theme))
    return undefined;
  const fallback = Object.hasOwn(SECTION_PADDING_DEFAULTS, section.type)
    ? SECTION_PADDING_DEFAULTS[section.type]
    : undefined;
  if (fallback === undefined || fallback === null) return undefined;
  const padding = hasExplicitSpacing(website.theme)
    ? undefined
    : legacyPadding(section, website, fallback);
  const margin = framedMargin(section);
  if (padding === undefined && margin === undefined) return undefined;
  return {
    ...(padding === undefined ? {} : { padding }),
    ...(margin === undefined ? {} : { margin }),
  };
}

function upgradedSections<S extends SpacingSection>(
  sections: S[] | undefined,
  website: SpacingWebsite,
): S[] | undefined {
  if (!Array.isArray(sections)) return sections;
  let changed = false;
  const next = sections.map((section) => {
    if (!section || typeof section !== "object") return section;
    const written = legacySpacing(section, website);
    if (!written) return section;
    changed = true;
    return { ...section, ui: { ...record(section.ui), ...written } };
  });
  return changed ? next : sections;
}

interface UpgradableWebsite extends SpacingWebsite {
  pages?: { sections?: SpacingSection[] }[];
  topbars?: { sections?: SpacingSection[] }[];
}

/**
 * Explicit spacing for a look-system website: every section of every page (system pages, footer pages and artikelside
 * templates included) and of every topbar gets the Luft indeni it is drawn with where it relies on its type's, and a
 * framed one its frame's margin as Luft udenom (`legacySpacing`), and the theme gets both markers,
 * `styleSystem.spacing.explicitSpacing: true` and `explicitFrame: true`. A website with explicit spacing but not the
 * frame marker gets Indrammet's step only: its framed sections' margins and the frame marker. It never changes a
 * website off the look system or one with both markers, so it is idempotent; the input is not modified.
 */
export function explicitSpacingUpgrade<T extends UpgradableWebsite>(
  cmsData: T,
): T {
  if (!isLookSystem(cmsData?.theme) || hasExplicitFrame(cmsData.theme))
    return cmsData;
  const withSections = <G extends { sections?: SpacingSection[] }>(
    groups: G[] | undefined,
  ) => {
    if (!Array.isArray(groups)) return groups;
    const next = groups.map((group) => {
      const sections = upgradedSections(group?.sections, cmsData);
      return sections === group?.sections ? group : { ...group, sections };
    });
    return next.some((group, index) => group !== groups[index]) ? next : groups;
  };
  const theme = record(cmsData.theme);
  const styleSystem = record(theme.styleSystem);
  return {
    ...cmsData,
    ...(cmsData.pages ? { pages: withSections(cmsData.pages) } : {}),
    ...(cmsData.topbars ? { topbars: withSections(cmsData.topbars) } : {}),
    theme: {
      ...theme,
      styleSystem: {
        ...styleSystem,
        spacing: {
          ...record(styleSystem.spacing),
          explicitSpacing: true,
          explicitFrame: true,
        },
      },
    },
  };
}
